import { TextSpan } from "components/text-label";
import { formatDate, formatWeekday } from "features/daily-delivery/utils";
import { useEffect, useState } from "react";
import intl from "react-intl-universal";
import type { BreweryTableData, BreweryTableRow } from "../../types";
import { Card } from "..";
import { DailyDeliveryModal } from "../DailyDeliveryModal";
import { ColTdFixed, ColTh, ColThFixed, StyledTable, TableScrollContainer } from "./styles";

interface DailyDeliveryTableProps {
  breweryTableData: BreweryTableData;
  familyId: number;
  handleSearchDailyDeliveries: () => void;
}

interface ModalState {
  open: boolean;
  date: string;
  familyId: number;
  idUnidadeNegocio: number;
}

export const DailyDeliveryTable = ({
  breweryTableData,
  familyId,
  handleSearchDailyDeliveries,
}: DailyDeliveryTableProps) => {
  const [modalState, setModalState] = useState<ModalState>({
    open: false,
    date: "",
    familyId: 0,
    idUnidadeNegocio: 0,
  });

  const handleCardClick = (date: string, familyId: number, idUnidadeNegocio: number) => {
    setModalState({
      open: true,
      date,
      familyId,
      idUnidadeNegocio,
    });
  };

  const handleCloseModal = () => {
    setModalState({
      open: false,
      date: "",
      familyId: 0,
      idUnidadeNegocio: 0,
    });
  };

  const handleScroll = () => {
    const scrollers = document.querySelectorAll(".daily-delivery-scroller");

    const scrollAll = (e: Event) => {
      scrollers.forEach((element) => {
        const target = element as HTMLElement;
        target.scrollLeft = (e.target as HTMLElement).scrollLeft;
      });
    };

    scrollers.forEach((element) => {
      element.addEventListener("scroll", scrollAll);
    });
  };

  useEffect(() => {
    handleScroll();
  }, []);

  const renderTableHeader = () => (
    <thead>
      <tr>
        <ColThFixed>
          <TextSpan
            color="var(--color-contrast-white)"
            display="block"
            style={{ fontSize: "14px", fontWeight: 700 }}
          >
            {intl.get("commons.destiny")}
          </TextSpan>
        </ColThFixed>
        {breweryTableData.columns.slice(1).map((date) => (
          <ColTh key={date}>
            <TextSpan
              color="var(--color-contrast-white)"
              display="block"
              style={{ fontSize: "14px", fontWeight: 700 }}
            >
              {formatWeekday(date)}
            </TextSpan>
            <TextSpan
              color="var(--color-contrast-white)"
              display="block"
              style={{ fontSize: "14px", fontWeight: 700 }}
            >
              {formatDate(date)}
            </TextSpan>
          </ColTh>
        ))}
      </tr>
    </thead>
  );

  const renderTableCell = (row: BreweryTableRow, date: string) => {
    const cellData = row[date];

    if (!cellData || typeof cellData === "string" || typeof cellData === "number") {
      return <td key={date} />;
    }

    return (
      <td key={date}>
        <Card
          variant={cellData.Status}
          onClick={() => handleCardClick(date, familyId, row.BreweryId)}
          clickable
        >
          <Card.Tag variant={cellData.Status} />
          <Card.DockCapacityIndicator
            variant={cellData.Status}
            totalExpected={cellData.TotalExpected}
            capacity={cellData.Capacity}
          />
          <Card.FooterIndicator
            ordersCount={cellData.OrdersCount}
            totalVolume={cellData.TotalVolume}
          />
        </Card>
      </td>
    );
  };

  const renderTableBody = () => (
    <tbody>
      {breweryTableData.rows.map((row) => (
        <tr key={row.BreweryId}>
          <ColTdFixed>
            <TextSpan
              color="var(--color-neutral-600)"
              display="block"
              style={{ fontSize: "14px", fontWeight: 400, padding: "30px" }}
            >
              {row.Brewery}
            </TextSpan>
          </ColTdFixed>
          {breweryTableData.columns.slice(1).map((date) => renderTableCell(row, date))}
        </tr>
      ))}
    </tbody>
  );

  return (
    <>
      <TableScrollContainer>
        <StyledTable>
          {renderTableHeader()}
          {renderTableBody()}
        </StyledTable>
      </TableScrollContainer>

      <DailyDeliveryModal
        open={modalState.open}
        onClose={handleCloseModal}
        date={modalState.date}
        familyId={modalState.familyId}
        idUnidadeNegocio={modalState.idUnidadeNegocio}
        handleSearchDailyDeliveries={handleSearchDailyDeliveries}
      />
    </>
  );
};
